import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { MessageSquare, Pencil, Phone, Plus, Star, Trash2 } from "lucide-react";
import { useNavigate } from "react-router";
import { useEffect, useState, type FormEvent } from "react";
import { del, get, patch, post, put, qs } from "../../api/client";
import type { Contact, Page } from "../../api/types";
import { useCan } from "../../auth/session";
import { Button } from "../../components/ui/Button";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { Dialog } from "../../components/ui/Dialog";
import { TextAreaField, TextField } from "../../components/ui/Field";
import { Card, EmptyState, ErrorState, InlineError, Loading, PageHeader, Pagination } from "../../components/ui/misc";
import { toast } from "../../components/ui/toast";
import { formatPhone } from "../../lib/format";
import { useDialer } from "../../voice/dialer";

export function ContactsPage() {
  const { can } = useCan();
  const qc = useQueryClient();
  const dial = useDialer((s) => s.dial);
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<Contact | "new" | null>(null);
  const [deleting, setDeleting] = useState<Contact | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const q = useQuery({
    queryKey: ["contacts", { search, favoritesOnly, page }],
    queryFn: () => get<Page<Contact>>(`/contacts${qs({ search, favoritesOnly, page, pageSize: 25 })}`),
    placeholderData: keepPreviousData,
  });

  const toggleFavorite = async (c: Contact) => {
    setTogglingId(c.id);
    try {
      if (c.isFavorite) await del(`/contacts/${c.id}/favorite`);
      else await put(`/contacts/${c.id}/favorite`);
      await qc.invalidateQueries({ queryKey: ["contacts"] });
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader
        title="Contacts"
        description="A shared directory for your organization. Favorites are personal."
        actions={can("contacts.create") && <Button variant="primary" onClick={() => setEditing("new")}><Plus className="h-4 w-4" aria-hidden="true" />Add contact</Button>}
      />
      <Card>
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <div className="min-w-60 flex-1"><TextField label="Search contacts" placeholder="Name, company, email or number" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} /></div>
          <label className="flex h-9 items-center gap-2 text-sm text-default">
            <input type="checkbox" className="accent-accent-600" checked={favoritesOnly} onChange={(e) => { setFavoritesOnly(e.target.checked); setPage(1); }} />
            Favorites only
          </label>
        </div>
        {q.isLoading ? <Loading /> : q.error ? <ErrorState error={q.error} onRetry={() => void q.refetch()} /> :
          q.data!.items.length === 0 ? (
            <EmptyState title={search || favoritesOnly ? "No matching contacts" : "No contacts yet"}
              action={can("contacts.create") && !search ? <Button size="sm" onClick={() => setEditing("new")}>Add the first contact</Button> : undefined} />
          ) : (
            <>
              <ul className="divide-y divide-[var(--border)]" aria-label="Contacts">
                {q.data!.items.map((c) => (
                  <li key={c.id} className="flex flex-wrap items-center gap-3 py-3">
                    <button
                      className="focus-ring rounded p-1 disabled:opacity-40"
                      aria-label={c.isFavorite ? `Remove ${c.name} from favorites` : `Add ${c.name} to favorites`}
                      aria-pressed={c.isFavorite}
                      disabled={!can("favorites.manage") || togglingId === c.id}
                      onClick={() => void toggleFavorite(c)}
                    >
                      <Star className={`h-4 w-4 ${c.isFavorite ? "fill-amber-400 text-amber-400" : "text-subtle"}`} />
                    </button>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-default">{c.name}</p>
                      <p className="truncate text-xs text-subtle">{[formatPhone(c.phone), c.company, c.email].filter(Boolean).join(" · ")}</p>
                    </div>
                    <div className="flex gap-1">
                      {can("messages.send") && (
                        <Button size="sm" onClick={() => navigate(`/messages?to=${encodeURIComponent(c.phone)}`)} aria-label={`Message ${c.name}`}>
                          <MessageSquare className="h-4 w-4" aria-hidden="true" />Text
                        </Button>
                      )}
                      <Button size="sm" variant="success" onClick={() => dial(c.phone)} disabled={!can("calls.initiate")} aria-label={`Call ${c.name}`}><Phone className="h-4 w-4" aria-hidden="true" />Call</Button>
                      {can("contacts.update") && <Button size="sm" variant="ghost" onClick={() => setEditing(c)} aria-label={`Edit ${c.name}`}><Pencil className="h-4 w-4" /></Button>}
                      {can("contacts.delete") && <Button size="sm" variant="ghost" onClick={() => setDeleting(c)} aria-label={`Delete ${c.name}`}><Trash2 className="h-4 w-4" /></Button>}
                    </div>
                  </li>
                ))}
              </ul>
              <Pagination page={q.data!.page} pageSize={q.data!.pageSize} total={q.data!.total} onPage={setPage} />
            </>
          )}
      </Card>
      {editing && <ContactDialog contact={editing === "new" ? null : editing} onClose={() => setEditing(null)} />}
      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(null)}
        title={`Delete ${deleting?.name ?? "contact"}?`}
        description="The contact is removed for everyone in your organization. Call history keeps the phone number."
        confirmLabel="Delete contact"
        onConfirm={async () => {
          await del(`/contacts/${deleting!.id}`);
          toast.success("Contact deleted");
          await qc.invalidateQueries({ queryKey: ["contacts"] });
        }}
      />
    </div>
  );
}

function ContactDialog({ contact, onClose }: { contact: Contact | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    name: contact?.name ?? "", phone: contact?.phone ?? "", email: contact?.email ?? "", company: contact?.company ?? "", notes: contact?.notes ?? "",
  });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const field = (k: keyof typeof form) => ({ value: form[k], onChange: (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value })) });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const body = { name: form.name, phone: form.phone, email: form.email || null, company: form.company || null, notes: form.notes || null };
    try {
      if (contact) await patch(`/contacts/${contact.id}`, body);
      else await post("/contacts", body);
      await qc.invalidateQueries({ queryKey: ["contacts"] });
      toast.success(contact ? "Contact updated" : "Contact added");
      onClose();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={contact ? "Edit contact" : "Add contact"} description="Only collect what you need to reach this person.">
      <form onSubmit={submit} className="space-y-3">
        <TextField label="Name" required maxLength={120} {...field("name")} autoFocus />
        <TextField label="Phone number" type="tel" required {...field("phone")} hint="US numbers can be entered without +1; include the country code otherwise." />
        <TextField label="Email (optional)" type="email" maxLength={254} {...field("email")} />
        <TextField label="Company (optional)" maxLength={120} {...field("company")} />
        <TextAreaField label="Notes (optional)" rows={3} maxLength={1000} {...field("notes")} />
        <InlineError error={error} />
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" loading={busy}>{contact ? "Save changes" : "Add contact"}</Button>
        </div>
      </form>
    </Dialog>
  );
}
